import type { UserType } from '@/lib/api/types'

// User type hierarchy levels
export const USER_TYPE_HIERARCHY: Record<UserType, number> = {
  'org-administrator': 100,
  'support': 50,
  'cash': 50,
  'vendor': 30,
  'customer': 10,
  'attendee': 10,
}

// Map dashboard feature permissions to API model permissions
// Dashboard permissions (keys) are granted if user has ANY of the listed API permissions (values)
export const API_PERMISSION_MAPPING: Record<string, string[]> = {
  // Events - vendors and vendor event admins can also view events they're associated with
  can_view_events: ['view_event', 'view_eventadministrator', 'is_vendor', 'view_vendoreventadministrator'],
  can_create_events: ['add_event'],
  can_edit_events: ['change_event'],
  can_delete_events: ['delete_event'],

  // Dashboard - vendors can view dashboard for their locations
  can_view_dashboard: ['can_view_dashboard', 'is_vendor', 'view_vendoreventadministrator'],

  // Toolkit (share links, QR, posters, best practices)
  can_view_toolkit: ['can_view_toolkit'],

  // Support (user management features)
  can_view_support: ['can_view_support'],

  // Reload/Financials (account management)
  can_view_reload: ['can_reload'],
  can_transfer: ['can_transfer'],
  can_view_accounts: ['view_userevent'],
  can_manage_accounts: ['change_userevent'],
  can_manage_roles: ['can_manage_roles'],
  can_see_user_info: ['can_see_user_info'],

  // Orders
  can_view_orders: ['view_order_list'],
  can_view_order_detail: ['view_order'],

  // Transactions
  can_view_transactions: ['view_transactionlog'],

  // Users
  can_view_users: ['can_view_users'],
  can_edit_users: ['change_user'],
  can_create_user: ['add_user'],
  can_view_user_orders: ['can_view_user_orders'],
  can_view_user_transactions: ['can_view_user_transactions'],
  can_view_user_items: ['can_view_user_items', 'view_redeemableitem'],
  can_view_payment_methods: ['can_view_payment_methods'],
  can_add_payment_methods: ['can_add_payment_methods'],
  can_view_user_wallet_pass: ['view_eventuserbarcode'],
  can_add_user_wallet_pass: ['add_eventuserbarcode'],
  can_delete_user_wallet_pass: ['delete_eventuserbarcode'],

  // Vendors (vendor list page)
  can_view_vendors: ['can_view_vendor_list', 'is_vendor', 'view_vendoreventadministrator'],
  can_create_vendors: ['add_vendorevent'],
  can_edit_vendors: ['change_vendor', 'change_vendorevent', 'change_vendoreventitem', 'change_vendoreventadministrator'],

  // Vendor Locations
  can_view_locations: ['can_view_vendor_locations', 'is_vendor', 'view_vendoreventadministrator'],
  can_view_vendor_events: ['can_view_vendor_events', 'is_vendor', 'view_vendoreventadministrator'],
  can_edit_locations: ['change_vendorevent'],

  // Menu (vendor event items)
  can_view_menu: ['view_vendoreventitem', 'view_eventcategory', 'is_vendor'],
  can_create_menu: ['add_vendoreventitem'],
  can_edit_menu: ['change_vendoreventitem', 'change_eventcategory'],
  can_delete_menu: ['delete_vendoreventitem'],
  can_update_item_price: ['can_update_item_price'],

  // Vendor Administrators
  can_add_vendor_admin: ['add_vendoradministrator'],
  can_change_vendor_admin: ['change_vendoradministrator'],
  can_delete_vendor_admin: ['delete_vendoradministrator'],

  // Vendor Event Administrators
  can_add_vendor_event_admin: ['add_vendoreventadministrator'],
  can_change_vendor_event_admin: ['change_vendoreventadministrator'],
  can_delete_vendor_event_admin: ['delete_vendoreventadministrator'],

  // Vouchers
  can_view_vouchers: ['view_tokenvoucher'],
  can_view_voucher_claims: ['view_voucherclaim'],
  can_create_vouchers: ['add_tokenvoucher'],
  can_edit_vouchers: ['change_tokenvoucher'],
  can_delete_vouchers: ['delete_tokenvoucher'],
  can_bulk_import_vouchers: ['bulk_import_tokenvoucher'],
  can_edit_voucher_claims: ['change_voucherclaim'],

  // Event Content (content pages)
  can_view_event_content: ['view_eventcontentpage'],
  can_edit_event_content: [
    'change_eventhomepage',
    'add_eventhomepage',
    'change_eventcontentpage',
    'add_eventcontentpage',
    'change_eventnavigation',
    'add_eventnavigation',
    'change_eventhomepagenavigation',
    'change_eventhomepagevendor',
    'change_eventquestions',
  ],
  can_delete_event_content: [
    'delete_eventhomepage',
    'delete_eventcontentpage',
    'delete_eventnavigation',
    'delete_eventhomepagenavigation',
  ],

  // Home Layout
  can_edit_home_layout: ['change_eventhomepage', 'change_eventnavigation', 'view_eventhomepage', 'view_eventnavigation'],
  can_add_navigation_item: ['add_eventhomepagenavigation', 'add_eventnavigation'],
  can_delete_navigation_item: ['delete_eventhomepagenavigation', 'delete_eventnavigation'],

  // Site Map
  can_view_site_map: ['can_view_site_map'],

  // POS (Point of Sale)
  can_view_pos: ['can_view_pos'],

  // Integrations — terminal codenames come from the backend `terminals` app
  // (PaymentDevice.Meta.permissions); printers are device-local.
  can_view_integrations: ['can_use_terminal', 'can_edit_terminal', 'view_paymentdevice'],
  // Card phones: approve phones for card payments. The codename is held
  // per event; the page and its nav entry also hide on the list's 403.
  can_manage_card_phones: ['can_add_terminal'],

  // Contacts — org-level attendee CRM. UX gate only; the backend endpoint
  // enforces OrganizationAdministrator membership. Codenames mirror Events so
  // the item resolves for admins; the sidebar additionally narrows by actual
  // org membership.
  can_view_contacts: ['view_friend', 'view_event', 'view_eventadministrator'],

  // Reporting
  can_view_reporting: ['can_view_reporting'],

  // Live Stats — event-level only (excludes vendor admins even if they have can_view_reporting)
  can_view_live_stats: ['view_eventadministrator'],

  // Financials
  can_view_financials: ['can_view_financials'],

  // Demographics
  can_view_demographics: ['view_usersegment', 'view_userevent'],

  // Data export
  can_export_data: ['view_userevent', 'view_vendorevent', 'view_cardpresenttransaction'],
  can_export_users: ['can_export_users'],
  can_export_transactions: ['can_export_transactions'],
  can_export_orders: ['can_export_orders'],
  can_export_vendor_logins: ['can_export_vendor_logins'],

  // Refund operations
  can_refund_addcredit: ['change_userevent'],
  can_refund_order: ['change_userevent'],

  // Vendor identification
  is_vendor: ['is_vendor'],

  // Support chat
  can_use_support_chat: ['can_use_support_chat'],

  // Fraud monitoring — codenames come from the backend `fraud` app
  // (FraudPolicy.Meta.permissions). View reads the dashboard; manage also
  // edits the policy, blocks attendees and records chargebacks. Org admins
  // and superusers pass server-side without either.
  can_view_fraud: ['can_view_fraud', 'can_manage_fraud'],
  can_manage_fraud: ['can_manage_fraud'],

  // Promotions (discount schedules) — codenames come from the backend
  // (event migration 0171 grants them to event admins). A vendor or location
  // admin holding them is scoped to their own menus. Edit does NOT imply view
  // here: the backend lists and previews schedules only for holders of
  // can_view_discounts. Org admins and superusers pass server-side without
  // either.
  can_view_discounts: ['can_view_discounts'],
  can_edit_discounts: ['can_edit_discounts'],
  // The Promotions page: its schedules need can_view_discounts, its Report and
  // Needs review tabs only reporting (the backend scopes those to the
  // viewer's vendors).
  can_view_promotions: ['can_view_discounts', 'can_view_reporting'],
}

// Navigation permissions - defines which nav items each permission maps to
export const NAVIGATION_PERMISSIONS: Record<string, string> = {
  // EventPay-internal ops overview. 'can_view_pulse' is a pseudo-codename no
  // role group grants, so hasPermission() resolves true only via the
  // superuser short-circuit — organizers can never satisfy it. (Unmapped
  // navKeys are denied outright, which is why this entry must exist at all.)
  pulse: 'can_view_pulse',
  revenue: 'can_view_pulse',
  new_event: 'can_view_pulse',
  events: 'can_view_events',
  dashboard: 'can_view_dashboard',
  toolkit: 'can_view_toolkit',
  support: 'can_view_support',
  reload: 'can_view_reload',
  orders: 'can_view_orders',
  transactions: 'can_view_transactions',
  users: 'can_view_users',
  vendors: 'can_view_vendors',
  vendor_locations: 'can_view_locations',
  locations: 'can_view_locations',
  menu: 'can_view_menu',
  reporting: 'can_view_reporting',
  financials: 'can_view_financials',
  fraud: 'can_view_fraud',
  promotions: 'can_view_promotions',
  demographics: 'can_view_demographics',
  accounts: 'can_manage_roles',
  vouchers: 'can_view_vouchers',
  event_content: 'can_view_event_content',
  integrations: 'can_view_integrations',
  card_phones: 'can_manage_card_phones',
  site_map: 'can_view_site_map',
  live_stats: 'can_view_live_stats',
  pos: 'can_view_pos',
  event_settings: 'can_edit_events',
  contacts: 'can_view_contacts',
  terminals_admin: 'can_view_pulse',
  fraud_admin: 'can_view_pulse',
  // Infrastructure metrics: same EventPay-internal audience as Pulse.
  system_admin: 'can_view_pulse',
  home_layout: 'can_edit_home_layout',
}

/**
 * Check if user has a specific permission
 */
const IDENTITY_FLAGS = new Set(['is_vendor'])

export function hasPermission(userPermissions: string[], permission: string, isSuperuser = false): boolean {
  // Superusers have all permissions, but identity flags must be checked against actual permissions
  if (isSuperuser && !IDENTITY_FLAGS.has(permission)) {
    return true
  }

  // Check if this is a mapped dashboard permission
  if (permission in API_PERMISSION_MAPPING) {
    const requiredApiPerms = API_PERMISSION_MAPPING[permission]
    return requiredApiPerms.some(apiPerm => userPermissions.includes(apiPerm))
  }

  // Check direct permission match
  if (userPermissions.includes(permission)) {
    return true
  }

  // Check without 'can_' prefix
  if (permission.startsWith('can_')) {
    const permWithoutCan = permission.replace('can_', '')
    if (userPermissions.includes(permWithoutCan)) {
      return true
    }
  }

  return false
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(userPermissions: string[], permissions: string[], isSuperuser = false): boolean {
  return permissions.some(perm => hasPermission(userPermissions, perm, isSuperuser))
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(userPermissions: string[], permissions: string[], isSuperuser = false): boolean {
  return permissions.every(perm => hasPermission(userPermissions, perm, isSuperuser))
}

/**
 * Check if user can access a specific navigation item
 */
export function canAccessNav(userPermissions: string[], navItem: string, isSuperuser = false): boolean {
  const requiredPermission = NAVIGATION_PERMISSIONS[navItem]
  if (!requiredPermission) {
    return false
  }
  return hasPermission(userPermissions, requiredPermission, isSuperuser)
}

/**
 * Get list of navigation items the user can access
 */
export function getAccessibleNavItems(userPermissions: string[], isSuperuser = false): string[] {
  return Object.keys(NAVIGATION_PERMISSIONS).filter(navItem =>
    canAccessNav(userPermissions, navItem, isSuperuser)
  )
}

/**
 * Check if current user can access/manage users of the target type
 * Users can only manage users at their level or below
 */
export function canAccessUserType(currentUserType: UserType, targetUserType: UserType): boolean {
  const currentLevel = USER_TYPE_HIERARCHY[currentUserType] || 0
  const targetLevel = USER_TYPE_HIERARCHY[targetUserType] || 0
  return currentLevel >= targetLevel
}
